import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { hrAPI } from "../services/api";

import {
UserCheck,
Calendar,
Mic,
MessageSquare,
BadgeCheck,
CheckCircle,
XCircle
} from "lucide-react";

const HRCandidateProfile = () => {

const { id } = useParams();
const navigate = useNavigate();

const [data,setData] = useState(null);
const [note,setNote] = useState("");
const [rating,setRating] = useState(5);
const [comments,setComments] = useState("");

useEffect(()=>{
loadProfile();
},[id]);

// ================= LOAD PROFILE =================

const loadProfile = async()=>{

try{

const res = await hrAPI.getApplicationDetails(id);

setData(res.data || res);

}catch(err){

console.error(err);

}

};


// ================= ADD NOTE =================

const addNote = async()=>{

if(!note) return;

try{

await hrAPI.addNote(id,{
text:note
});

setNote("");

loadProfile();

}catch(err){

console.error(err);

}

};


// ================= SUBMIT FEEDBACK =================

const submitFeedback = async()=>{

try{

await hrAPI.addFeedback(id,{
rating,
comments
});

setComments("");

loadProfile();

}catch(err){

console.error(err);

}

};


// ================= CHANGE STAGE =================

const changeStage = async(stage)=>{

try{

await hrAPI.updateApplicationStatus(id,stage);

loadProfile();

}catch(err){

console.error(err);

}

};


if(!data) return null;

const profile = data.profile || {};
const application = data.application || {};
const user = data.user || {};
const job = data.job || {};

const resume =
profile.resumeUrl ||
application.resumeUrl;

return(

<div className="p-6 bg-gray-50 min-h-screen">

{/* HEADER */}

<Card className="p-6 mb-6">

<div className="flex items-center gap-4">

{/* Avatar */}

<div className="w-20 h-20 rounded-full overflow-hidden bg-purple-600 flex items-center justify-center text-white text-2xl font-bold">

{profile.photo ? (

<img
src={`${import.meta.env.VITE_BACKEND_URL}/uploads/${profile.photo}`}
alt="profile"
className="w-full h-full object-cover"
/>

):(user.fullName?.charAt(0))}

</div>


{/* User Info */}

<div>

<h1 className="text-2xl font-bold">
{user.fullName}
</h1>

<p className="text-gray-500">
{user.email}
</p>

<p className="text-gray-500">
{user.mobile} • {profile.location || "N/A"}
</p>

</div>


{/* Navigation */}

<div className="ml-auto flex gap-2">

<Button
variant="outline"
onClick={()=>navigate("/hr/dashboard")}
>
Close
</Button>


<Button
variant="outline"
onClick={()=>navigate(-1)}
>
Back
</Button>

<Button
variant="outline"
onClick={()=>navigate(`/hr/candidate/previous/${id}`)}
>
Previous
</Button>

<Button
variant="outline"
onClick={()=>navigate(`/hr/candidate/next/${id}`)}
>
Next
</Button>

</div>

</div>

</Card>


<div className="grid grid-cols-3 gap-6">

{/* LEFT COLUMN */}

<div className="col-span-2 space-y-6">

{/* Candidate Insights */}

<Card className="p-6">

<h2 className="font-semibold mb-4">
Candidate Insights
</h2>

<div className="grid grid-cols-2 gap-6">

<div>

<p className="text-sm text-gray-500">
Experience
</p>

<div className="w-full bg-gray-200 h-2 rounded mt-2">

<div
className="bg-purple-500 h-2 rounded"
style={{ width:`${(profile.experience || 1)*10}%` }}
></div>

</div>

<p className="text-sm mt-2">
{profile.experience || "N/A"}
</p>

</div>


<div>

<p className="text-sm text-gray-500">
Notice Period
</p>

<span className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm">

{profile.noticePeriod || "N/A"}

</span>

</div>

</div>

</Card>


{/* Current Job */}

<Card className="p-6">

<h2 className="font-semibold mb-4">
Current Job
</h2>

<p className="font-medium">
{profile.currentRole || "Not Available"}
</p>

<p className="text-gray-500">
{profile.currentCompany || job.companyName || application.currentCompany||"Company Not Provided"}
</p>

</Card>


{/* CTC */}

<Card className="p-6">

<h2 className="font-semibold mb-4">
CTC Comparison
</h2>

<div className="flex gap-4">

<span className="px-4 py-2 bg-orange-100 text-orange-700 rounded">
₹ {profile.currentPackage || "N/A"}
</span>

<span className="px-4 py-2 bg-green-100 text-green-700 rounded">
₹ {profile.expectedPackage || "N/A"}
</span>

</div>

</Card>


{/* Timeline */}

<Card className="p-6">

<h2 className="font-semibold mb-4">
Candidate Timeline
</h2>

<div className="space-y-4">

{application.stageHistory?.map((item,i)=>(

<div key={i} className="flex gap-3">

<div className="w-3 h-3 mt-2 rounded-full bg-purple-500"></div>

<div>

<p className="font-medium capitalize">
{item.stage}
</p>

<p className="text-xs text-gray-500">
{new Date(item.changedAt).toLocaleString()}
</p>

<p className="text-xs text-gray-400">
by {item.changedBy}
</p>

</div>

</div>

))}

</div>

</Card>


{/* Recruiter Notes */}

<Card className="p-6">

<h2 className="font-semibold mb-4">
Recruiter Notes
</h2>

<textarea
className="w-full border rounded p-2"
placeholder="Write recruiter notes..."
value={note}
onChange={(e)=>setNote(e.target.value)}
></textarea>

<Button
className="mt-2"
onClick={addNote}
>
Add Note
</Button>


<div className="mt-4 space-y-2">

{application.notes?.map((n,i)=>(

<div key={i} className="bg-gray-50 p-3 rounded">

<p>{n.text}</p>

<p className="text-xs text-gray-400">
{n.createdBy} • {new Date(n.createdAt).toLocaleDateString()}
</p>

</div>

))}

</div>

</Card>


{/* Feedback */}

<Card className="p-6">

<h2 className="font-semibold mb-4">
Interview Feedback
</h2>

<select
className="border rounded p-2"
value={rating}
onChange={(e)=>setRating(e.target.value)}
>

<option value="1">1</option>
<option value="2">2</option>
<option value="3">3</option>
<option value="4">4</option>
<option value="5">5</option>

</select>

<textarea
className="w-full border rounded p-2 mt-2"
placeholder="Interview comments"
value={comments}
onChange={(e)=>setComments(e.target.value)}
></textarea>

<Button
className="mt-2"
onClick={submitFeedback}
>
Submit Feedback
</Button>

<div className="mt-4 space-y-3">

{application.feedback?.map((f,i)=>(

<div key={i} className="bg-gray-50 p-3 rounded">

<p>⭐ Rating: {f.rating}/5</p>

<p>{f.comments}</p>

<p className="text-xs text-gray-400">
{f.createdBy}
</p>

</div>

))}

</div>

</Card>

</div>


{/* RIGHT COLUMN */}

<div className="space-y-6">

{/* Skills */}

<Card className="p-6">

<h2 className="font-semibold mb-4">
Skills
</h2>

<div className="flex flex-wrap gap-2">

{profile.skills?.map((skill,i)=>(

<span
key={i}
className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded"
>
{skill}
</span>

))}

</div>

</Card>


{/* Resume */}

<Card className="p-6">

<Button
className="w-full"
onClick={()=>window.open(`${import.meta.env.VITE_BACKEND_URL}${resume}`,"_blank")}
>
View Resume
</Button>

</Card>


{/* Actions */}

<Card className="p-6 space-y-3">

<Button onClick={()=>changeStage("shortlisted")} className="w-full flex gap-2 items-center">
<UserCheck size={16}/> Shortlist
</Button>

<Button onClick={()=>changeStage("scheduled")} className="w-full flex gap-2 items-center">
<Calendar size={16}/> Schedule
</Button>

<Button onClick={()=>changeStage("interviewed")} className="w-full flex gap-2 items-center">
<Mic size={16}/> Interview
</Button>

<Button onClick={()=>changeStage("feedback")} className="w-full flex gap-2 items-center">
<MessageSquare size={16}/> Feedback
</Button>

<Button onClick={()=>changeStage("offer")} className="w-full flex gap-2 items-center">
<BadgeCheck size={16}/> Offer
</Button>

<Button onClick={()=>changeStage("hired")} className="w-full flex gap-2 items-center">
<CheckCircle size={16}/> Hire
</Button>

<Button onClick={()=>changeStage("rejected")} className="w-full flex gap-2 items-center">
<XCircle size={16}/> Reject
</Button>

</Card>

</div>

</div>

</div>

);

};

export default HRCandidateProfile;